import { AddToCalendarButton } from 'add-to-calendar-button-react';
import cn from 'classnames';

import { useDictionary } from 'src/hooks/useDictionary';
import { useRouteContext } from 'src/hooks/useRouteContext';

import {
  formatDateForCalendar,
  formatStartEndDateTime,
  formatTimeForCalendar,
  isFutureEvent,
} from 'src/utils/formatDate';

import Cta from 'components/BaseHelpers/Cta';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import { ComponentProps } from '../../../types/component-props';
import { CoreEventDetailsData } from '../../../types/components';

import styles from './CoreEventDetails.module.scss';

export type CoreEventDetailsProps = ComponentProps & CoreEventDetailsData;

const CoreEventDetails = (props: CoreEventDetailsProps): JSX.Element => {
  const {
    label,
    eventName,
    startDateTime,
    endDateTime,
    showTimeOfDay,
    displayDate,
    location,
    street,
    city,
    stateOrProvince,
    postalCode,
    googleMapsUrl,
    registerUrl,
    registerLabel,
    ctaUrl,
    ctaLabel,
    timeZone,
  } = props.fields;

  const { routeData } = useRouteContext();

  const {
    dateLabel,
    timeLabel,
    locationLabel,
    registerLabel: dictionaryRegisterLabel,
    getDirectionsLabel,
    addToCalendarLabel,
  } = useDictionary();

  const formattedDate = formatStartEndDateTime(startDateTime, endDateTime, displayDate);

  const addressHtml = [
    location,
    street,
    [[city, stateOrProvince].filter(Boolean).join(', '), postalCode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join('<br />')
    .replace(/\r?\n/g, '<br />');

  const formattedDisplayDate = showTimeOfDay
    ? formattedDate.display
    : formattedDate.dateOnlyDisplay;
  const hasDate = Boolean(formattedDisplayDate);

  if (!hasDate && !addressHtml && !registerUrl) {
    return <></>;
  }

  const isFuture = isFutureEvent(startDateTime, endDateTime);
  const startDateForCalendar = formatDateForCalendar(startDateTime);
  const startTimeForCalendar = formatTimeForCalendar(startDateTime);
  const endDateForCalendar = formatDateForCalendar(endDateTime);
  const endTimeForCalendar = formatTimeForCalendar(endDateTime);

  return (
    <div className={styles.main}>
      <Text className={cn(styles['header'], 'body-lg')} field={label} />
      <div className={styles.details}>
        {formattedDate.date ? (
          <>
            <div className={styles['detail-item']}>
              <Text className={cn(styles['subheader'], 'body-med')} field={dateLabel} />
              <Text className={cn('body-med')} field={formattedDate.date} />
            </div>
            {showTimeOfDay && (
              <div className={styles['detail-item']}>
                <Text className={cn(styles['subheader'], 'body-med')} field={timeLabel} />
                <Text className={cn('body-med')} field={formattedDate.time} />
              </div>
            )}
          </>
        ) : formattedDisplayDate ? (
          <div className={styles['detail-item']}>
            <Text className={cn(styles['subheader'], 'body-med')} field={dateLabel} />
            <Text className={cn('body-med')} field={formattedDisplayDate} />
          </div>
        ) : null}
        {addressHtml && (
          <div className={styles['detail-item']}>
            <Text className={cn(styles['subheader'], 'body-med')} field={locationLabel} />
            <RichText className={'body-med'} field={addressHtml} styled={false} />
          </div>
        )}
      </div>
      {googleMapsUrl && isFuture && (
        <Cta
          tag="a"
          href={googleMapsUrl}
          openNewTab={true}
          className={styles.register}
          showIcon={false}
        >
          {getDirectionsLabel}
        </Cta>
      )}
      {registerUrl && isFuture && (
        <Cta
          tag="a"
          href={registerUrl}
          openNewTab={true}
          className={styles.register}
          showIcon={false}
        >
          {registerLabel || dictionaryRegisterLabel}
        </Cta>
      )}
      {ctaUrl && !isFuture && (
        <Cta tag="a" href={ctaUrl} openNewTab={true} className={styles.register} showIcon={false}>
          {ctaLabel}
        </Cta>
      )}
      {isFuture &&
        startDateForCalendar &&
        startTimeForCalendar &&
        endDateForCalendar &&
        endTimeForCalendar && (
          <div className={styles['calendar-container']}>
            <AddToCalendarButton
              name={eventName}
              description={
                routeData?.route?.url
                  ? `[url]${routeData.route.url}|${routeData.route.url}[/url]`
                  : undefined
              }
              startDate={startDateForCalendar}
              startTime={startTimeForCalendar}
              endDate={endDateForCalendar}
              endTime={endTimeForCalendar}
              options={['Apple', 'Google', 'iCal', 'Microsoft365', 'MicrosoftTeams', 'Outlook.com']}
              timeZone={timeZone}
              listStyle="overlay"
              forceOverlay={true}
              hideBackground={true}
              hideIconButton={true}
              hideBranding={true}
              hideCheckmark={true}
              size="6|5|4"
              trigger="click"
              label={addToCalendarLabel}
              identifier="add-to-calendar-button"
            />
            <Cta tag="span" className={styles['calendar-cta']} showIcon={false} aria-hidden="true">
              {addToCalendarLabel}
            </Cta>
          </div>
        )}
    </div>
  );
};

export default CoreEventDetails;
